import { useEffect, useCallback } from 'react';
import { useBreadcrumbContextSafe } from '../components/Breadcrumb/BreadcrumbProvider';

type BreadcrumbKey =
  | 'equipment'
  | 'deterioration'
  | 'schedule'
  | 'work-order-template'
  | 'work-history'
  | 'emergency-work'
  | 'part'
  | 'supplier'
  | 'purchase-order'
  | 'company'
  | 'agency'
  | string;

interface UseBreadcrumbReturn {
  /** 動的タイトルを設定 */
  setTitle: (key: BreadcrumbKey, title: string) => void;
  /** 動的タイトルを取得 */
  getTitle: (key: BreadcrumbKey) => string | undefined;
  /** 動的タイトルをクリア */
  clearTitle: (key: BreadcrumbKey) => void;
}

/**
 * パンくずリストの動的タイトルを設定するためのカスタムフック
 *
 * 使用例:
 * ```tsx
 * function EquipmentDetailPage() {
 *   const { data: equipment } = useEquipmentQuery(id);
 *   const { setTitle } = useBreadcrumb();
 *
 *   // 設備名をパンくずリストに反映
 *   useBreadcrumbTitle('equipment', equipment?.name);
 *
 *   return <div>...</div>;
 * }
 * ```
 */
export function useBreadcrumb(): UseBreadcrumbReturn {
  const { setDynamicTitle, getDynamicTitle, clearDynamicTitle } =
    useBreadcrumbContextSafe();

  const setTitle = useCallback(
    (key: BreadcrumbKey, title: string) => {
      setDynamicTitle(key, title);
    },
    [setDynamicTitle]
  );

  const getTitle = useCallback(
    (key: BreadcrumbKey) => getDynamicTitle(key),
    [getDynamicTitle]
  );

  const clearTitle = useCallback(
    (key: BreadcrumbKey) => clearDynamicTitle(key),
    [clearDynamicTitle]
  );

  return { setTitle, getTitle, clearTitle };
}

/**
 * パンくずリストの動的タイトルを自動設定するフック
 * コンポーネントのアンマウント時に自動的にクリア
 *
 * 使用例:
 * ```tsx
 * function EquipmentDetailPage() {
 *   const { data: equipment } = useEquipmentQuery(id);
 *
 *   // 設備名をパンくずリストに反映（アンマウント時に自動クリア）
 *   useBreadcrumbTitle('equipment', equipment?.name);
 *
 *   return <div>...</div>;
 * }
 * ```
 */
export function useBreadcrumbTitle(
  key: BreadcrumbKey,
  title: string | undefined | null
): void {
  const { setDynamicTitle, clearDynamicTitle } = useBreadcrumbContextSafe();

  useEffect(() => {
    if (title) {
      setDynamicTitle(key, title);
    }

    // コンポーネントのアンマウント時にクリア
    return () => {
      clearDynamicTitle(key);
    };
  }, [key, title, setDynamicTitle, clearDynamicTitle]);
}
